import { useEffect, useState } from "react";
import { BellRinging, BellSlash, SpeakerHigh, SpeakerSlash, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  pushSupported, isSubscribed, enablePush, disablePush,
  chimeEnabled, setChime, playChime,
} from "@/lib/push";

export function NotificationBell() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chime, setChimeState] = useState(chimeEnabled());
  const supported = pushSupported();

  useEffect(() => { isSubscribed().then(setOn).catch(() => {}); }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      const d = event.data || {};
      if (d.type === "guestivate-push") {
        playChime();
        toast(d.payload?.title || "New notification", { description: d.payload?.body });
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (on) {
        await disablePush();
        setOn(false);
        toast.success("Notifications turned off");
      } else {
        await enablePush();
        setOn(true);
        playChime();
        toast.success("Notifications on — you'll be alerted when a guest needs a human");
      }
    } catch (e) {
      const m = e.message === "denied" ? "Permission blocked — enable it in your browser settings"
        : e.message === "unsupported" ? "Not supported in this browser"
        : e.message === "not-configured" ? "Push isn't configured on the server"
        : "Couldn't enable notifications";
      toast.error(m);
    } finally { setBusy(false); }
  };

  const toggleChime = () => {
    const v = !chime;
    setChime(v);
    setChimeState(v);
    if (v) playChime();
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test sent — you should see a notification");
    } catch { toast.error("Couldn't send test"); }
  };

  if (!supported) return null;

  return (
    <div data-testid="notif-control" className="flex items-center gap-1 rounded-md border border-border bg-background px-1 py-1">
      <button
        data-testid="notif-toggle"
        onClick={toggle}
        disabled={busy}
        title={on ? "Front-desk notifications are on" : "Enable front-desk notifications"}
        className={`flex items-center gap-2 flex-1 rounded px-2.5 py-1.5 text-[12.5px] font-medium transition-colors ${on ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
      >
        {on ? <BellRinging size={16} weight="fill" /> : <BellSlash size={16} />}
        <span className="truncate">{on ? "Alerts on" : "Enable alerts"}</span>
      </button>
      {on && (
        <button data-testid="notif-test" onClick={sendTest} title="Send a test notification"
          className="shrink-0 rounded px-1.5 py-1.5 text-muted-foreground hover:text-foreground transition-colors">
          <PaperPlaneTilt size={15} />
        </button>
      )}
      <button
        data-testid="chime-toggle"
        onClick={toggleChime}
        title={chime ? "Chime on" : "Chime off"}
        className={`shrink-0 rounded px-1.5 py-1.5 transition-colors ${chime ? "text-foreground" : "text-muted-foreground/60 hover:text-foreground"}`}
      >
        {chime ? <SpeakerHigh size={15} weight="fill" /> : <SpeakerSlash size={15} />}
      </button>
    </div>
  );
}
